/**
 * Edge function endpoint constants.
 *
 * Single source of truth for every Supabase Edge Function path consumed by the
 * query layer. Names are upper-snake; values are the slug after
 * `/functions/v1/`. Import from `_edge.js` or any query file instead of
 * sprinkling the raw string literal across the codebase.
 *
 * Example:
 *   import { CHAT } from './_endpoints'
 *   await invokeEdgeStream(CHAT, payload, { signal })
 */

export const CHAT = 'chat'
export const SYNTHESIZE = 'synthesize'
export const TITLE_GEN = 'title-gen'
export const TRUNCATE_CONVERSATION = 'truncate-conversation'
export const NEXT_STEP = 'ministry-next-step'
export const VERIFY_SESSION = 'verify-session'
export const BILLING_PORTAL = 'billing-portal'
export const HEALTH_CHECK = 'health-check'
export const ANALYTICS_DIGEST = 'analytics-digest'
export const ADMIN_CONTENT_JOBS = 'admin-content-jobs'
export const SCHEDULED_CLEANUP = 'scheduled-cleanup'
export const CONTACT_FORM = 'contact-form'
export const EXPORT_USER_DATA = 'export-user-data'
export const ADMIN_FINANCE = 'admin-finance'
export const PLATFORM_INGESTION = 'platform-ingestion'
export const DAILY_ANALYTICS_PULL = 'daily-analytics-pull'
export const FETCH_BIBLE_CHAPTER = 'fetch-bible-chapter'
export const MAILERLITE_SUBSCRIBE = 'mailerlite-subscribe'
export const GENERATE_PROMPT_TUNING_REPORT = 'generate-prompt-tuning-report'
export const GENERATE_CONTENT_GAP_REPORT = 'generate-content-gap-report'
export const TRIAGE_SUPPORT_TICKET = 'triage-support-ticket'
export const SUMMARIZE_BLOG_POST = 'summarize-blog-post'
export const GENERATE_PERSON_PROFILE = 'generate-person-profile'
export const PEOPLE_AI_DEEP_DIVE = 'people-ai-deep-dive'
export const GENERATE_CONTENT_INSIGHT = 'generate-content-insight'
export const GENERATE_PACKAGE = 'generate-package'
export const GENERATE_DIAGNOSTIC = 'generate-diagnostic'
export const TOPIC_SEARCH = 'topic-search'
export const CONTENT_GENERATE_OUTLINE = 'content-generate-outline'
export const CONTENT_GENERATE_SCRIPT = 'content-generate-script'
export const CONTENT_REWRITE_SECTION = 'content-rewrite-section'
export const CONTENT_REWRITE_FULL = 'content-rewrite-full'
export const CONTENT_INTERVIEW_PROMPT = 'content-interview-prompt'
export const CONTENT_EXTRACT_PATTERN = 'content-extract-pattern'
export const CONTENT_ATOMIZE_LONGFORM = 'content-atomize-longform'

/**
 * Complete deployed Supabase edge function registry. Distinct purpose
 * from the named constants above:
 *   - Named constants are the subset the query layer actually invokes
 *     from browser code (used as typed args to invokeEdge / invokeEdgeStream).
 *   - This array is the FULL deployed-function manifest, including the
 *     cron-only / webhook / scheduled functions never called from the
 *     browser. Surfaces the count on the admin System Health page and
 *     anchors deploy-audit scripts that walk every deployed function.
 *
 * Keep in sync with the supabase project — every CLI deploy/undeploy
 * that adjusts the function set belongs here too. Consolidates what
 * used to live separately in src/data/edgeFunctions.js (deleted as
 * part of ORG-051: two sources of truth for endpoint URLs is a drift
 * bug waiting to happen).
 */
export const EDGE_FUNCTIONS = [
  'admin-content-jobs',
  'admin-finance',
  'analytics-digest',
  'billing-portal',
  'chat',
  'contact-form',
  'content-atomize-longform',
  'content-extract-pattern',
  'content-generate-outline',
  'content-generate-script',
  'content-interview-prompt',
  'content-redirector',
  'content-rewrite-full',
  'content-rewrite-section',
  'daily-analytics-pull',
  'daily-post-digest',
  'draft-generation',
  'draft-qa',
  'export-user-data',
  'fetch-bible-chapter',
  'generate-content-gap-report',
  'generate-content-insight',
  'generate-diagnostic',
  'generate-package',
  'generate-person-profile',
  'generate-prompt-tuning-report',
  'health-check',
  'hook-performance-aggregator',
  'mailerlite-subscribe',
  'ministry-next-step',
  'people-ai-deep-dive',
  'platform-ingestion',
  'produce-approved',
  'push-daily-cron',
  'retry-failed-jobs',
  'scheduled-cleanup',
  'scripture-verify',
  'stripe-webhook',
  'subscription-reconcile',
  'summarize-blog-post',
  'synthesize',
  'tiktok-sunday-scrape',
  'title-gen',
  'topic-search',
  'triage-support-ticket',
  'truncate-conversation',
  'verify-session',
  'weekly-performance',
  'weekly-research',
  'weekly-spend-report',
  'x-posts-generation',
  'youtube-trending',
]

export const EDGE_FUNCTION_COUNT = EDGE_FUNCTIONS.length
